/**
 * useInviteNotifications Hook
 *
 * React hook for managing invite notifications.
 * Handles real-time invite events and provides methods to accept/decline.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PresenceSocket, getSharedPresenceSocket, releaseSharedPresenceSocket } from '../services/presence-socket';
import type { InviteNotification } from '@drawn-of-war/shared';

/**
 * Hook return type
 */
export interface UseInviteNotificationsResult {
  /** Array of pending invites */
  invites: InviteNotification[];
  /** Accept an invite */
  acceptInvite: (inviteId: string) => Promise<void>;
  /** Decline an invite */
  declineInvite: (inviteId: string) => Promise<void>;
  /** Loading state for invite actions */
  isLoading: boolean;
  /** Error message if any action failed */
  error: string | null;
  /** Clear error */
  clearError: () => void;
}

/**
 * useInviteNotifications Hook
 *
 * Manages invite notifications including:
 * - Fetching pending invites on mount
 * - Real-time updates via Socket.IO
 * - Accept/decline functionality
 * - Auto-remove expired invites
 *
 * @returns Object with invites array and action methods
 */
export function useInviteNotifications(): UseInviteNotificationsResult {
  const [invites, setInvites] = useState<InviteNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const socketRef = useRef<PresenceSocket | null>(null);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch pending invites from API
  const fetchPendingInvites = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!user) return;

    try {
      const token = await getIdToken();
      if (!token) return; // No token available yet

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/invites/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvites(data.invites);
      }
    } catch (err) {
      console.error('[useInviteNotifications] Failed to fetch pending invites:', err);
    }
  }, [user, getIdToken]);

  // Accept an invite
  const acceptInvite = useCallback(async (inviteId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/invites/${inviteId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('[useInviteNotifications] Accept response:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to accept invite');
      }

      // Remove invite from list
      setInvites(prev => prev.filter(i => i.id !== inviteId));

      // Navigate to battle deploy page
      if (data.redirectTo) {
        console.log('[useInviteNotifications] Navigating to:', data.redirectTo);
        navigate(data.redirectTo);
      } else {
        console.warn('[useInviteNotifications] No redirectTo in response');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to accept invite';
      setError(errorMessage);
      console.error('[useInviteNotifications] Accept failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getIdToken, navigate]);

  // Decline an invite
  const declineInvite = useCallback(async (inviteId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/invites/${inviteId}/decline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to decline invite');
      }

      // Remove invite from list
      setInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to decline invite';
      setError(errorMessage);
      console.error('[useInviteNotifications] Decline failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [getIdToken]);

  // Setup socket listeners and fetch initial invites
  useEffect(() => {
    // Don't setup until user is authenticated
    if (!user) return;

    // Fetch pending invites on mount
    fetchPendingInvites();

    // Get shared socket connection for real-time updates
    const socket = getSharedPresenceSocket();
    socketRef.current = socket;

    // New invite received
    socket.onInviteReceived((invite: InviteNotification) => {
      console.log('[useInviteNotifications] Invite received:', invite);
      setInvites(prev => {
        // Avoid duplicates
        const exists = prev.some(i => i.id === invite.id);
        if (exists) return prev;
        return [...prev, invite];
      });
    });

    // Invite accepted (by us or someone else)
    socket.onInviteAccepted((data) => {
      console.log('[useInviteNotifications] Invite accepted:', data);
      // Already handled in acceptInvite, but good for external notifications
    });

    // Invite declined
    socket.onInviteDeclined((data) => {
      console.log('[useInviteNotifications] Invite declined:', data);
      // Already handled in declineInvite, but good for external notifications
    });

    // Invite expired
    socket.onInviteExpired((data) => {
      console.log('[useInviteNotifications] Invite expired:', data);
      setInvites(prev => prev.filter(i => i.id !== data.inviteId));
    });

    // Setup expiration checker (runs every 30 seconds)
    const expirationInterval = setInterval(() => {
      const now = new Date();
      setInvites(prev =>
        prev.filter(invite => new Date(invite.expiresAt) > now)
      );
    }, 30000);

    // Cleanup on unmount — remove listeners, release shared ref
    return () => {
      if (socketRef.current) {
        socketRef.current.offInviteListeners();
        socketRef.current = null;
      }
      releaseSharedPresenceSocket();
      clearInterval(expirationInterval);
    };
  }, [user, fetchPendingInvites]);

  return {
    invites,
    acceptInvite,
    declineInvite,
    isLoading,
    error,
    clearError
  };
}

/**
 * useCountdown Hook
 *
 * Returns a formatted countdown string from an expiration date.
 */
export function useCountdown(expiresAt: string): string {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft('Expired');
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  return timeLeft;
}
